import { useState } from 'react'
import { Phone, Mail, MessageCircle, MapPin, Clock, ChevronDown, ChevronUp, AlertTriangle, Users, Star, ArrowRight } from 'lucide-react'
import SEOHead from '../components/SEOHead'
import ContactForm from '../components/ContactForm'

export default function Contact() {
  const [openFaq, setOpenFaq] = useState(null)

  const contactMethods = [
    {
      icon: <Phone className="h-8 w-8 text-c9-orange" />,
      title: "Call Us",
      description: "Speak to our team directly",
      contact: "0208 152 4574",
      action: "tel:02081524574",
      hours: "Mon-Fri: 8:00 AM - 6:00 PM"
    },
    {
      icon: <Mail className="h-8 w-8 text-c9-orange" />,
      title: "Email Us", 
      description: "Send us a message anytime",
      contact: "contact@c9-recruitment.com",
      action: "mailto:contact@c9-recruitment.com",
      hours: "We respond within 24 hours"
    },
    {
      icon: <MapPin className="h-8 w-8 text-c9-orange" />,
      title: "Visit Us",
      description: "Drop by our office",
      contact: "Albany House, 162-168 High Street, Waltham Cross, EN8 7DF",
      action: "#",
      hours: "By appointment only"
    },
    {
      icon: <Users className="h-8 w-8 text-c9-orange" />,
      title: "Book a Meeting",
      description: "Schedule a consultation",
      contact: "Book Now",
      action: "mailto:contact@c9-recruitment.com?subject=Meeting Request",
      hours: "Available Mon-Fri"
    }
  ]

  const departments = [
    {
      name: "General Enquiries",
      description: "For all general questions and information",
      email: "contact@c9-recruitment.com",
      phone: "0208 152 4574"
    },
    {
      name: "Commercial Team",
      description: "For business partnerships and commercial inquiries",
      email: "commercial@c9-recruitment.com",
      phone: "0208 152 4574"
    },
    {
      name: "Recruitment Team", 
      description: "For recruitment services and job placements",
      email: "recruitment@c9-recruitment.com",
      phone: "0208 152 4574"
    },
    {
      name: "Legal Team",
      description: "For legal matters and compliance issues",
      email: "legal@c9-recruitment.com",
      phone: "0208 152 4574"
    }
  ]

  const faqs = [
    {
      question: "How quickly can you fill a driver position?",
      answer: "We can often provide same-day emergency cover and typically fill permanent positions within 48-72 hours, depending on specific requirements."
    },
    {
      question: "What areas do you cover?",
      answer: "We provide nationwide coverage across the UK, with particular strength in major transport hubs and industrial areas."
    },
    {
      question: "Do you handle all compliance checks?",
      answer: "Yes, we handle all compliance requirements including DBS checks, right to work verification, license validation, and reference checks."
    },
    {
      question: "What are your fees?",
      answer: "Our fees are competitive and transparent. We offer different pricing structures for permanent, temporary, and contract placements. Contact us for a personalized quote."
    },
    {
      question: "How do I apply for positions?",
      answer: "You can apply through our Candidates page, call us directly, or email your CV. We respond to all applications within 24 hours."
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Contact C9 Recruitment - Get in Touch Today"
        description="Contact C9 Recruitment for expert transport and logistics staffing solutions. Multiple contact methods available including 24/7 emergency cover."
        keywords="contact C9 recruitment, transport staffing, logistics recruitment, emergency cover, UK recruitment"
        canonical="/contact"
      />

      {/* Floating Background Shapes */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="animate-float absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-c9-orange/20 to-c9-orange-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-1 absolute top-1/3 right-20 w-32 h-32 bg-gradient-to-br from-c9-blue/20 to-c9-blue-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-2 absolute bottom-1/4 left-1/4 w-24 h-24 bg-gradient-to-br from-c9-orange/20 to-c9-orange-alt/20 rounded-full blur-xl"></div>
        <div className="animate-float-delay-3 absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-c9-blue/20 to-c9-blue-alt/20 rounded-full blur-xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-c9-blue via-c9-blue-alt to-c9-blue text-white section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Star className="h-4 w-4 text-c9-orange" />
              <span>Available 24/7 for Urgent Recruitment Needs</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent animate-fade-in-up">
              Get In <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Touch</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed animate-fade-in-up delay-100">
              Ready to transform your transport and logistics operations? Our expert team is here to help you find the perfect staffing solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
              <a 
                href="tel:02081524574" 
                className="btn-primary bg-gradient-to-r from-c9-orange to-c9-orange-alt hover:from-c9-orange-alt hover:to-c9-orange transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Call Now: 0208 152 4574
                <Phone className="ml-2 h-5 w-5" />
              </a>
              <a 
                href="#contact-form" 
                className="btn-secondary bg-white/10 backdrop-blur-sm border-2 border-white/20 text-white hover:bg-white hover:text-c9-blue transform hover:scale-105 transition-all duration-300"
              >
                Send Message
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Multiple Ways to <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Connect</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Choose the contact method that works best for you. We're available when you need us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <div key={index} className="group animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <a 
                  href={method.action}
                  className="block bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 text-center transform hover:-translate-y-2 h-full"
                >
                  <div className="bg-gradient-to-br from-c9-orange/10 to-c9-orange-alt/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    {method.icon}
                  </div>
                  <h3 className="text-xl font-bold text-c9-blue mb-3 group-hover:text-c9-orange transition-colors duration-300">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {method.description}
                  </p>
                  <div className="text-c9-orange font-semibold mb-3">
                    {method.contact}
                  </div>
                  <div className="text-sm text-gray-500">
                    {method.hours}
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-full">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
                Send Us a <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Message</span>
              </h2>
              <p className="text-xl text-gray-600 animate-fade-in-up delay-100">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>
            </div>
            
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-in-up delay-200">
              <div className="bg-gradient-to-r from-c9-orange to-c9-orange-alt p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-2">Let's Start a Conversation</h3>
                <p className="text-white/90">We're here to help with all your recruitment needs</p>
              </div>
              <div className="p-8">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="section-padding bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6 animate-fade-in-up">
              Our <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Departments</span>
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Specialized teams ready to handle your specific needs with expert knowledge and dedicated support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {departments.map((dept, index) => (
              <div key={index} className="group bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-c9-orange transition-colors duration-300">
                  {dept.name}
                </h3>
                <p className="text-white/80 mb-6 leading-relaxed">
                  {dept.description}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-c9-orange flex-shrink-0" />
                    <a 
                      href={`mailto:${dept.email}`}
                      className="text-white/90 hover:text-c9-orange transition-colors duration-300"
                    >
                      {dept.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-c9-orange flex-shrink-0" />
                    <a 
                      href={`tel:${dept.phone.replace(/\s/g, '')}`}
                      className="text-white/90 hover:text-c9-orange transition-colors duration-300"
                    >
                      {dept.phone}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Frequently Asked <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up delay-100">
              Quick answers to common questions about our services and processes.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg mb-4 overflow-hidden animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                <button
                  className="w-full p-6 text-left flex items-center justify-between focus:outline-none hover:bg-gray-50 transition-colors duration-300"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-bold text-c9-blue text-lg">{faq.question}</span>
                  <ChevronDown 
                    className={`h-6 w-6 text-c9-orange transition-transform duration-300 ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <p className="text-gray-600 leading-relaxed pt-4">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact CTA */}
      <section className="section-padding bg-gradient-to-br from-c9-orange to-c9-orange-alt text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        <div className="container-full relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <AlertTriangle className="h-4 w-4" />
              <span>24/7 Emergency Support Available</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6 animate-fade-in-up">
              Need Urgent Staffing?
            </h2>
            <p className="text-xl text-white/90 mb-8 animate-fade-in-up delay-100">
              We provide round-the-clock emergency cover for urgent staffing requirements. Our dedicated team is always ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up delay-200">
              <a 
                href="tel:02081524574" 
                className="btn-secondary bg-white text-c9-orange hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl text-lg px-8 py-4"
              >
                📞 Call Now: 0208 152 4574
              </a>
              <div className="text-white/80 text-sm">
                Available 24/7 for urgent cover
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-full">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-c9-blue mb-6 animate-fade-in-up">
              Our Coverage <span className="bg-gradient-to-r from-c9-orange to-c9-orange-alt bg-clip-text text-transparent">Area</span>
            </h2>
            <p className="text-xl text-gray-600 animate-fade-in-up delay-100">
              Comprehensive recruitment services across the United Kingdom
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 text-center animate-fade-in-up delay-200">
              <div className="bg-gradient-to-br from-c9-orange/10 to-c9-orange-alt/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8">
                <MapPin className="h-10 w-10 text-c9-orange" />
              </div>
              <h3 className="text-3xl font-bold text-c9-blue mb-6">Nationwide Coverage</h3>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                From Scotland to the South Coast, we provide comprehensive recruitment services across all major transport and logistics hubs throughout the UK.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  "London & South East",
                  "Midlands", 
                  "North West",
                  "North East",
                  "Wales",
                  "Scotland", 
                  "South West",
                  "Yorkshire"
                ].map((region, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 bg-c9-orange rounded-full flex-shrink-0"></div>
                    <span className="text-sm font-medium">{region}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
